import { alpha, createTheme } from "@mui/material/styles";

/**
 * Single source of truth for typography across the whole app.
 * Import this anywhere you need to match the global type scale in sx props:
 *   import { typographyScale } from "../styles/theme";
 *   sx={{ fontSize: typographyScale.fontSize.md }}
 */
export const typographyScale = {
  fontSize: {
    xs:   "0.6875rem",  // 11px — captions, overlines, table col headers
    sm:   "0.75rem",    // 12px — body2, secondary text, chips
    base: "0.8125rem",  // 13px — body1, primary reading text
    md:   "0.875rem",   // 14px — h6, prominent labels, tab names
    lg:   "1rem",       // 16px — h5, section headings
    xl:   "1.125rem",   // 18px — h4 / h3, sub-page headings
    "2xl":"1.5rem",     // 24px — h1 / h2, page titles
  },
  fontWeight: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },
  lineHeight: {
    tight:  1.25,
    normal: 1.5,
  },
};

// Day: white work surface with a light-grey sidebar (`elevated`); cool blue
// primary, violet secondary, teal highlight. Night: deep navy with the same
// hues lifted so they read as glowing accents on the dark surface.
// The per-module accent colours live in constants/moduleVisuals.js.
export const engineeringTokens = {
  light: {
    bg: { default: "#ffffff", paper: "#ffffff", elevated: "#f7f9fc" },
    border: { subtle: "#e2e8f0", strong: "#cbd5e1" },
    accent: {
      primary: "#2563eb",
      primaryHover: "#1d4ed8",
      secondary: "#7c3aed",
      secondaryHover: "#6d28d9",
      highlight: "#14b8a6",
    },
    text: { primary: "#0f172a", secondary: "#475569" },
  },
  dark: {
    bg: { default: "#0b1120", paper: "#111827", elevated: "#0f172a" },
    border: { subtle: "#1e293b", strong: "#334155" },
    accent: {
      primary: "#3b82f6",
      primaryHover: "#60a5fa",
      secondary: "#a78bfa",
      secondaryHover: "#c4b5fd",
      highlight: "#22d3ee",
    },
    text: { primary: "#e2e8f0", secondary: "#94a3b8" },
  },
};

const scrollbarStyles = (mode) => {
  const tokens = engineeringTokens[mode] ?? engineeringTokens.light;
  const track = tokens.bg.elevated;
  const thumb = tokens.border.strong;
  const thumbHover = tokens.accent.primary;

  return {
    scrollbarWidth: "thin",
    scrollbarColor: `${thumb} ${track}`,
    "&::-webkit-scrollbar": { width: 10, height: 10 },
    "&::-webkit-scrollbar-track": { backgroundColor: track },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: thumb,
      borderRadius: 6,
      border: `2px solid ${track}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
  };
};

export const createAppTheme = (mode = "light") => {
  const isDark = mode === "dark";
  const tokens = isDark ? engineeringTokens.dark : engineeringTokens.light;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.accent.primary,
        light: isDark ? "#93c5fd" : "#60a5fa",
        dark: tokens.accent.primaryHover,
        contrastText: "#ffffff",
      },
      secondary: {
        main: tokens.accent.secondary,
        light: isDark ? "#ddd6fe" : "#a78bfa",
        dark: tokens.accent.secondaryHover,
        contrastText: "#ffffff",
      },
      background: {
        default: tokens.bg.default,
        paper: tokens.bg.paper,
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
      },
      divider: tokens.border.subtle,
      success: {
        main: isDark ? "#34d399" : "#059669",
      },
      warning: {
        main: isDark ? "#fbbf24" : "#F59E0B",
        contrastText: isDark ? "#0c1017" : "#FFFFFF",
      },
      error: {
        main: isDark ? "#DC2626" : "#DC2626",
      },
      info: {
        main: isDark ? "#60a5fa" : "#3B82F6",
      },
      action: {
        hover: alpha(tokens.accent.primary, isDark ? 0.1 : 0.06),
        selected: alpha(tokens.accent.primary, isDark ? 0.18 : 0.1),
      },
    },
    typography: {
      fontFamily: '"Open Sans", "Segoe UI", Roboto, sans-serif',
      htmlFontSize: 16,
      h1: { fontSize: typographyScale.fontSize["2xl"], fontWeight: typographyScale.fontWeight.bold,     lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.02em" },
      h2: { fontSize: typographyScale.fontSize["2xl"], fontWeight: typographyScale.fontWeight.bold,     lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.02em" },
      h3: { fontSize: typographyScale.fontSize.xl,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.tight,  letterSpacing: "-0.01em" },
      h4: { fontSize: typographyScale.fontSize.xl,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.tight },
      h5: { fontSize: typographyScale.fontSize.lg,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal },
      h6: { fontSize: typographyScale.fontSize.md,     fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal },
      subtitle1: { fontSize: typographyScale.fontSize.md,   fontWeight: typographyScale.fontWeight.medium,   lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.01em" },
      subtitle2: { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.08em", textTransform: "uppercase" },
      body1:     { fontSize: typographyScale.fontSize.base, fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal },
      body2:     { fontSize: typographyScale.fontSize.sm,   fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal },
      button:    { fontSize: typographyScale.fontSize.sm,   fontWeight: typographyScale.fontWeight.semibold, letterSpacing: "0.02em", textTransform: "none" },
      caption:   { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.regular,  lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.04em", fontFamily: '"Roboto Mono", "JetBrains Mono", monospace' },
      overline:  { fontSize: typographyScale.fontSize.xs,   fontWeight: typographyScale.fontWeight.semibold, lineHeight: typographyScale.lineHeight.normal, letterSpacing: "0.08em", fontFamily: '"Roboto Mono", "JetBrains Mono", monospace' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: mode,
            // PV data tables (Shell / Nozzle) paint their own header and
            // border colours through these, so a table that builds plain
            // style objects - no theme in hand - still follows the theme.
            // Every hand-built table header in the modules (muiProps presets,
            // BOM / MIS / PMS column defs, …) reads these, so tables follow the
            // day/night theme without each one checking the mode.
            "--pv-table-header-bg": isDark ? "#172033" : "#eff4fb",
            "--pv-table-header-text": tokens.text.primary,
            "--pv-table-border": isDark ? "#243044" : tokens.border.subtle,
          },
          body: {
            backgroundColor: tokens.bg.default,
            color: tokens.text.primary,
            ...scrollbarStyles(mode),
          },
          "#wrapper.app": {
            backgroundColor: tokens.bg.default,
            color: tokens.text.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
            // With the day background now white too, cards are told apart by
            // a hairline and a soft shadow rather than by a grey backdrop.
            boxShadow: isDark
              ? `0 0 0 1px ${tokens.border.subtle}`
              : `0 0 0 1px ${tokens.border.subtle}, 0 1px 3px rgba(15, 23, 42, 0.06)`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.paper,
            borderBottom: `1px solid ${tokens.border.subtle}`,
            boxShadow: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.bg.elevated,
            borderRight: `1px solid ${tokens.border.subtle}`,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--pv-table-header-bg)",
            "& .MuiTableCell-head": {
              backgroundColor: "var(--pv-table-header-bg)",
              color: tokens.text.primary,
              fontWeight: 600,
              letterSpacing: "0.01em",
              borderColor: tokens.border.subtle,
              borderBottom: `1px solid ${tokens.border.strong}`,
            },
          },
        },
      },
      MuiTableFooter: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
            "& .MuiTableCell-footer": {
              backgroundColor: tokens.bg.paper,
              color: tokens.text.primary,
              borderColor: tokens.border.subtle,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
          toolbar: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
            color: tokens.text.primary,
            // Fixed-width digits so quantities, costs and dates line up down
            // a column.
            fontVariantNumeric: "tabular-nums",
          },
          head: {
            fontWeight: 600,
            backgroundColor: "var(--pv-table-header-bg)",
            color: tokens.text.primary,
          },
          body: {
            backgroundColor: "inherit",
          },
          footer: {
            backgroundColor: tokens.bg.paper,
            color: tokens.text.primary,
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            "& .MuiTableRow-root": {
              backgroundColor: tokens.bg.paper,
            },
            // Faint zebra striping keeps long registers readable across a row;
            // hover and selection still win over it.
            "& .MuiTableRow-root:nth-of-type(even)": {
              backgroundColor: isDark ? "#0f1729" : "#f8fafc",
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: alpha(tokens.accent.primary, isDark ? 0.1 : 0.05),
            },
            "& .MuiTableRow-root.Mui-selected": {
              backgroundColor: alpha(tokens.accent.primary, isDark ? 0.16 : 0.08),
            },
            "& .MuiTableCell-body": {
              backgroundColor: "inherit",
              color: tokens.text.primary,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.bg.paper,
            "&.Mui-selected": {
              backgroundColor: alpha(tokens.accent.primary, isDark ? 0.14 : 0.08),
            },
            "&.Mui-selected:hover": {
              backgroundColor: alpha(tokens.accent.primary, isDark ? 0.2 : 0.12),
            },
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "outlined-indigo" },
            style: {
              backgroundColor: tokens.bg.paper,
              border: `1px solid #6366f1`,
              color: "#6366f1",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 6,
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(99, 102, 241, 0.12)"
                  : "rgba(99, 102, 241, 0.06)",
                borderColor: "#818cf8",
                color: "#818cf8",
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
          },
          containedPrimary: {
            backgroundColor: tokens.accent.primary,
            color: "#ffffff",
            boxShadow: isDark
              ? `0 0 16px ${alpha(tokens.accent.primary, 0.35)}`
              : "none",
            "&:hover": {
              backgroundColor: tokens.accent.primaryHover,
            },
          },
          containedSecondary: {
            backgroundColor: tokens.accent.secondary,
            "&:hover": {
              backgroundColor: tokens.accent.secondaryHover,
            },
          },
          outlinedPrimary: {
            borderColor: alpha(tokens.accent.primary, 0.6),
            color: tokens.accent.primary,
            "&:hover": {
              borderColor: tokens.accent.primary,
              backgroundColor: alpha(tokens.accent.primary, isDark ? 0.1 : 0.06),
            },
          },
          outlinedSecondary: {
            borderColor: alpha(tokens.accent.secondary, 0.55),
            color: tokens.accent.secondary,
            "&:hover": {
              borderColor: tokens.accent.secondary,
              backgroundColor: alpha(tokens.accent.secondary, isDark ? 0.1 : 0.06),
            },
          },
          textPrimary: {
            color: tokens.accent.primary,
          },
          textSecondary: {
            color: tokens.accent.secondary,
          },
          outlined: {
            borderColor: tokens.border.strong,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? tokens.bg.elevated : tokens.bg.paper,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.border.subtle,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.border.strong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.accent.primary,
              borderWidth: 1,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: tokens.bg.paper,
            border: `1px solid ${tokens.border.strong}`,
            boxShadow: isDark
              ? `0 24px 48px ${alpha("#000000", 0.5)}`
              : undefined,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            borderBottom: `1px solid ${tokens.border.subtle}`,
          },
          indicator: {
            height: 3,
            borderRadius: "3px 3px 0 0",
            backgroundColor: tokens.accent.primary,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            fontWeight: 600,
            letterSpacing: "0.02em",
            "&.Mui-selected": {
              color: tokens.accent.primary,
              fontWeight: 700,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: '"Roboto Mono", monospace',
            fontSize: "0.7rem",
            borderRadius: 4,
          },
          outlined: {
            borderColor: tokens.border.strong,
          },
          colorInfo: isDark
            ? {
                backgroundColor: alpha("#60a5fa", 0.18),
                color: "#bfdbfe",
                border: `1px solid ${alpha("#60a5fa", 0.5)}`,
              }
            : {},
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? tokens.bg.elevated : "#1a2332",
            border: `1px solid ${tokens.border.strong}`,
            fontSize: "0.75rem",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            border: `1px solid ${tokens.border.subtle}`,
          },
        },
      },
    },
  });
};

/** @deprecated Prefer createAppTheme(colorMode) via AppThemeProvider */
export const theme = createAppTheme("light");
